import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';

import { useAlertsStore } from '@tallo/design-system';

import { CompanyRole } from '../enums/company-role.enum';
import { CompanyTeamMemberDto, InviteMemberDto, PendingInviteDto } from '../interfaces/team.dto';
import { companyPortfoliosQueryKey } from '../queries/company-portfolios.query';
import { companyTeamQueryKey } from '../queries/company-team.query';
import teamService from '../services/team.service';

/**
 * Every team change refreshes the roster; a removal also moves portfolios. The caller refreshes
 * what this lib cannot reach (the property list, each unit's team). The change itself already
 * happened, so a failed refresh is not an error.
 */
function refresh(...keys: string[]) {
  const queryCache = useQueryCache();

  keys.forEach((key) => queryCache.invalidateQueries({ key: [key] }).catch(() => undefined));
}

function reportError(error: unknown) {
  useAlertsStore().catchError(error);
}

/** The invite form turns a refused address into a field error itself, so nothing is reported here. */
export const useInviteMember = defineMutation(() => {
  return useMutation({
    mutation: (dto: InviteMemberDto) => teamService.invite(dto),
    onSuccess: () => refresh(companyTeamQueryKey),
  });
});

/** A refusal usually means someone accepted or revoked it meanwhile, so the roster is read again either way. */
export const useResendInvite = defineMutation(() => {
  return useMutation({
    mutation: (inviteId: string) => teamService.resendInvite(inviteId),
    onError: reportError,
    onSettled: () => refresh(companyTeamQueryKey),
  });
});

export const useRevokeInvite = defineMutation(() => {
  return useMutation({
    mutation: (invite: PendingInviteDto) => teamService.revokeInvite(invite.id),
    onError: reportError,
    onSettled: () => refresh(companyTeamQueryKey),
  });
});

export const useChangeMemberRole = defineMutation(() => {
  return useMutation({
    mutation: (payload: { member: CompanyTeamMemberDto; role: CompanyRole }) =>
      teamService.changeMemberRole(payload.member.investorId, payload.role),
    onError: reportError,
    onSettled: () => refresh(companyTeamQueryKey),
  });
});

/** The caller refreshes each unit's team, which this lib cannot reach. */
export const useAddMemberToPortfolios = defineMutation(() => {
  return useMutation({
    mutation: (payload: { member: CompanyTeamMemberDto; portfolioIds: string[] }) =>
      teamService.addToPortfolios(payload.member.investorId, payload.portfolioIds),
    onSuccess: () => refresh(companyTeamQueryKey, companyPortfoliosQueryKey),
    onError: reportError,
  });
});

export const useRemoveMember = defineMutation(() => {
  return useMutation({
    mutation: (payload: { member: CompanyTeamMemberDto; transferTo: string }) =>
      teamService.removeMember(payload.member.investorId, { transferTo: payload.transferTo }),
    onSuccess: () => refresh(companyTeamQueryKey, companyPortfoliosQueryKey),
    onError: reportError,
  });
});
